/**
 * Z-Report SMS Share Page (Feb 2026, v2 — PIN-gated, FULL DETAILED on-screen)
 *
 * Tap the SMS / Telegram link → /zr/<token> renders this page.
 * No app login required — the visitor enters their PIN once, gets a 24h
 * device-bound cookie, then sees the FULL detailed Z-Report (all 11 sections
 * matching the downloadable PDF) plus a Download Detailed PDF button.
 *
 * PIN accepts: Admin / Manager / Auditor PIN OR 6-digit Google Authenticator
 * code. Filtered server-side by org_id + branch_id (multi-tenant safe).
 *
 * Mobile-first: stacked rows, compact tables, sticky download bar.
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Download, Lock, Calendar, Building2, AlertTriangle, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const formatPHP = (n) => `\u20B1${Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ZReportSharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  // PIN gate state
  const [pinRequired, setPinRequired] = useState(false);
  const [pinTitle, setPinTitle] = useState('');
  const [pinValue, setPinValue] = useState('');
  const [pinError, setPinError] = useState('');
  const [attemptsLeft, setAttemptsLeft] = useState(5);
  const [locked, setLocked] = useState(false);
  const [lockMinutes, setLockMinutes] = useState(15);
  const [verifying, setVerifying] = useState(false);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${API}/api/zreport-share/${token}`, { withCredentials: true });
      setData(res.data);
      setPinRequired(false);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 401 && detail?.pin_required) {
        setPinRequired(true);
        setPinTitle(detail.title || 'Secure Z-Report');
        setAttemptsLeft(detail.attempts_left ?? 5);
        setLocked(!!detail.locked);
        setLockMinutes(detail.lock_minutes || 15);
      } else if (status === 410) {
        setError(typeof detail === 'string' ? detail : 'Link revoked or expired.');
      } else {
        const msg = (typeof detail === 'string' ? detail : detail?.message) || e.message || 'Failed to load report';
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const submitPin = async (e) => {
    e?.preventDefault?.();
    if (!pinValue) return;
    setVerifying(true);
    setPinError('');
    try {
      await axios.post(
        `${API}/api/zreport-share/${token}/verify`,
        { pin: pinValue },
        { withCredentials: true }
      );
      // Success — cookie now set; reload payload
      setPinValue('');
      await loadReport();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) {
        setLocked(true);
        setLockMinutes(detail?.lock_minutes || 15);
        setPinError(`Too many attempts. Try again in ${detail?.lock_minutes || 15} minutes.`);
      } else if (status === 401) {
        const left = detail?.attempts_left ?? Math.max(0, attemptsLeft - 1);
        setAttemptsLeft(left);
        setPinError(`Invalid PIN. ${left} attempt${left === 1 ? '' : 's'} remaining.`);
      } else {
        setPinError(e?.message || 'Verification failed');
      }
    } finally {
      setVerifying(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    setDownloadError('');
    try {
      const res = await axios.get(`${API}/api/zreport-share/${token}/pdf`, {
        responseType: 'blob', timeout: 60_000, withCredentials: true,
      });
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `Z-Report ${data?.branch_name || ''} ${data?.date || ''}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const status = e?.response?.status;
      let msg = e?.response?.data?.detail || '';
      if (!msg && e?.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text())?.detail || ''; } catch { /* ignore */ }
      }
      msg = msg || e.message || 'Download failed';
      if (status === 410) setError(msg);
      else if (status === 403) {
        // PIN expired — show PIN entry again
        setPinRequired(true);
        setData(null);
        setDownloadError('');
      } else setDownloadError(msg);
    } finally {
      setDownloading(false);
    }
  };

  // ── PIN entry view ─────────────────────────────────────────────────────────
  if (pinRequired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-7 max-w-sm w-full" data-testid="zr-pin-entry">
          <div className="flex items-center gap-2 text-emerald-700 mb-2">
            <Lock size={20} />
            <h1 className="text-base font-bold">Secure Report Access</h1>
          </div>
          <p className="text-xs text-slate-500 mb-4 leading-relaxed">{pinTitle}</p>
          <form onSubmit={submitPin} autoComplete="off">
            <label className="text-xs text-slate-600 font-semibold block mb-1">PIN or 6-digit Authenticator code</label>
            <input
              type="password" inputMode="numeric" autoComplete="one-time-code"
              data-lpignore="true" autoFocus
              value={pinValue}
              onChange={(e) => setPinValue(e.target.value)}
              disabled={locked || verifying}
              className="w-full px-3 py-3 text-lg font-mono tracking-widest text-center border-2 border-slate-200 rounded-xl focus:border-emerald-500 focus:outline-none disabled:bg-slate-100"
              data-testid="zr-pin-input"
              minLength={4} maxLength={32} required
            />
            <button
              type="submit" disabled={locked || verifying || !pinValue}
              className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold py-3 rounded-xl disabled:opacity-50 transition-colors"
              data-testid="zr-pin-submit"
            >
              {verifying ? 'Verifying\u2026' : 'Unlock Report'}
            </button>
            {locked && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="zr-pin-locked">
                Too many attempts. Try again in {lockMinutes} minutes.
              </div>
            )}
            {pinError && !locked && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="zr-pin-error">
                {pinError}
              </div>
            )}
            <div className="mt-4 text-[11px] text-slate-400 leading-relaxed">
              Use your admin / manager / auditor PIN or the 6-digit code from Google Authenticator. Access is scoped to this organization. Session lasts 24 hours on this device.
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="zr-share-loading">
        <div className="text-center text-slate-500 text-sm">
          <div className="w-10 h-10 border-4 border-emerald-300 border-t-emerald-600 rounded-full animate-spin mx-auto mb-3" />
          Loading Z-Report\u2026
        </div>
      </div>
    );
  }

  if (error || !data) {
    const isRevoked = /revoked|expired/i.test(error || '');
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="zr-share-error">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 max-w-md w-full text-center">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 ${isRevoked ? 'bg-amber-100' : 'bg-red-100'}`}>
            {isRevoked ? <Lock className="text-amber-600" size={28} /> : <AlertTriangle className="text-red-600" size={28} />}
          </div>
          <h1 className="text-lg font-bold text-slate-800 mb-1">
            {isRevoked ? 'Link No Longer Available' : 'Could Not Load Report'}
          </h1>
          <p className="text-sm text-slate-500 leading-relaxed">{error || 'The Z-Report could not be loaded.'}</p>
        </div>
      </div>
    );
  }

  const c = data.closing || {};
  const d = data.detailed || {};
  const isOpen = data.is_open;

  return (
    <div className="min-h-screen bg-slate-50 pb-32" data-testid="zr-share-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 text-white px-5 py-6 shadow-md">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-1.5 text-emerald-200 text-[11px] uppercase tracking-wider font-semibold">
            <Building2 size={11} /> {data.company_name || 'Z-Report'}
          </div>
          <h1 className="text-xl font-black mt-0.5 leading-tight">{data.branch_name}</h1>
          <div className="flex items-center gap-1.5 text-emerald-100 text-sm mt-0.5">
            <Calendar size={13} /> {data.date}
          </div>
          <div className="flex items-center gap-1.5 text-emerald-200 text-[10px] mt-2">
            <ShieldCheck size={11} /> Verified · 24h session active
            {data.recipient_name && <span className="italic">· Sent to {data.recipient_name}</span>}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-3 py-4 space-y-3">
        {isOpen && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
            <p className="font-semibold">\u26A0 Closing not yet sealed</p>
            <p className="mt-0.5">This day has not been finalized. The data shown is the live preview.</p>
          </div>
        )}

        {/* 1. KPI Tiles */}
        <div className="grid grid-cols-2 gap-2" data-testid="zr-share-kpis">
          <Tile label="Total Sales" value={formatPHP(c.total_sales || c.total_cash_sales || 0)} accent="emerald" />
          <Tile label="Drawer Actual" value={formatPHP(c.actual_cash || 0)} accent="blue" />
          <Tile label="Expected Cash" value={formatPHP(c.expected_counter || 0)} accent="violet" />
          <Tile label="Variance" value={formatPHP(c.over_short || 0)} accent={Math.abs(c.over_short || 0) < 1 ? 'emerald' : 'red'} />
        </div>

        {/* 2. Cash Drawer Reconciliation */}
        <Section title="Cash Drawer Reconciliation">
          <Row label="Starting Float" value={formatPHP(c.starting_float || 0)} />
          <Row label="+ Cash Sales" value={formatPHP(c.total_cash_sales || 0)} />
          <Row label="+ Split-Cash Portion" value={formatPHP(c.total_split_cash || 0)} />
          <Row label="+ AR Cash Payments" value={formatPHP(c.total_cash_ar || c.total_ar_received || 0)} highlight />
          {(c.total_ar_same_day || 0) > 0 && <Row label="\u21B3 Same-Day Credit Pmts" value={formatPHP(c.total_ar_same_day)} sub />}
          {(c.total_ar_older || 0) > 0 && <Row label="\u21B3 Older Credit Pmts" value={formatPHP(c.total_ar_older)} sub />}
          <Row label="\u2212 Cashier Expenses" value={`-${formatPHP(c.total_cashier_expenses || 0)}`} negative />
          <Row label="Net Fund Transfers" value={formatPHP(c.net_fund_transfers || 0)} />
          <div className="border-t border-dashed border-slate-300 my-2" />
          <Row label="EXPECTED CASH" value={formatPHP(c.expected_counter || 0)} bold />
          <Row label="ACTUAL CASH" value={formatPHP(c.actual_cash || 0)} bold />
          <Row label="OVER / SHORT" value={formatPHP(c.over_short || 0)} bold negative={(c.over_short || 0) < 0} />
        </Section>

        {/* 3. End-of-Day Vault Allocation */}
        {(d.vault_allocation?.to_safe || d.vault_allocation?.float_for_tomorrow) > 0 && (
          <Section title="End-of-Day Vault Allocation">
            <Row label="\u2192 To Safe / Vault" value={formatPHP(d.vault_allocation.to_safe || 0)} />
            <Row label="\u2192 Float for Tomorrow" value={formatPHP(d.vault_allocation.float_for_tomorrow || 0)} />
          </Section>
        )}

        {/* 4. AR Payments Received */}
        {Array.isArray(d.ar_payments) && d.ar_payments.length > 0 && (
          <Section title={`AR Payments Received (${d.ar_payments.length})`}>
            {d.ar_payments.map((p, i) => (
              <DetailLine key={i}
                primary={p.customer_name || p.customer || '\u2014'}
                secondary={`${p.invoice_number || ''}${p.method ? '  \u00B7  ' + p.method : ''}`}
                value={formatPHP(p.amount || 0)}
                valueClass="text-emerald-700"
              />
            ))}
          </Section>
        )}

        {/* 5. Interest & Penalty Invoices Issued Today */}
        {Array.isArray(d.interest_invoices) && d.interest_invoices.length > 0 && (
          <Section title={`Interest & Penalty Invoices (${d.interest_invoices.length})`}>
            {d.interest_invoices.map((p, i) => (
              <DetailLine key={i}
                primary={p.customer_name || p.customer || '\u2014'}
                secondary={`${p.invoice_number || ''}${p.type ? '  \u00B7  ' + p.type : ''}`}
                value={formatPHP(p.amount || 0)}
                valueClass="text-amber-700"
              />
            ))}
          </Section>
        )}

        {/* 6. Fund Transfers */}
        {Array.isArray(d.fund_transfers) && d.fund_transfers.length > 0 && (
          <Section title={`Fund Transfers (${d.fund_transfers.length})`}>
            {d.fund_transfers.map((ft, i) => (
              <DetailLine key={i}
                primary={ft.type || ft.transfer_type || 'Transfer'}
                secondary={[ft.authorized_by || ft.approver, ft.note].filter(Boolean).join('  \u00B7  ')}
                value={formatPHP(ft.amount || 0)}
                valueClass="text-slate-700"
              />
            ))}
          </Section>
        )}

        {/* 7. Cashier Expenses */}
        {(c.expenses || []).length > 0 && (
          <Section title={`Cashier Expenses (${c.expenses.length})`}>
            {c.expenses.map((e, i) => (
              <DetailLine key={i}
                primary={e.description || e.category || 'Expense'}
                secondary={[e.category, e.fund_source].filter(Boolean).join('  \u00B7  ')}
                value={`-${formatPHP(e.amount || 0)}`}
                valueClass="text-red-600"
              />
            ))}
            <div className="border-t border-dashed border-slate-300 my-2" />
            <Row label="Total Expenses" value={formatPHP(c.total_expenses || 0)} bold negative />
          </Section>
        )}

        {/* 8. Credit Extended Today */}
        {(c.total_credit_today || 0) > 0 && (
          <Section title="Credit Extended Today">
            <Row label="Total Credit Outstanding" value={formatPHP(c.total_credit_today || 0)} bold />
            <Row label="Total Invoice Value" value={formatPHP(c.total_credit_invoice_value || 0)} sub muted />
            {(c.credit_sales_today || []).map((inv, i) => (
              <DetailLine key={i}
                primary={inv.customer_name || '\u2014'}
                secondary={`${inv.invoice_number || ''}${inv.status ? '  \u00B7  ' + inv.status : ''}`}
                value={formatPHP(inv.balance || 0)}
                valueClass="text-amber-700"
              />
            ))}
          </Section>
        )}

        {/* 9. Cash Sales by Category */}
        {Array.isArray(d.cash_sales_by_category) && d.cash_sales_by_category.length > 0 && (
          <Section title="Cash Sales by Category">
            <div className="grid grid-cols-12 gap-1 text-[10px] uppercase tracking-wider text-slate-400 font-bold pb-1 border-b border-slate-100">
              <div className="col-span-6">Category</div>
              <div className="col-span-2 text-right">Items</div>
              <div className="col-span-4 text-right">Total</div>
            </div>
            {d.cash_sales_by_category.map((cat, i) => (
              <div key={i} className="grid grid-cols-12 gap-1 text-xs py-1 border-b border-slate-50">
                <div className="col-span-6 text-slate-700 truncate">{cat.category || cat.name || '\u2014'}</div>
                <div className="col-span-2 text-right text-slate-500">{cat.items_sold ?? cat.units ?? cat.qty ?? 0}</div>
                <div className="col-span-4 text-right text-slate-800">{formatPHP(cat.total || cat.amount || 0)}</div>
              </div>
            ))}
          </Section>
        )}

        {/* 10. Discounts Given Today */}
        {d.discounts && ((d.discounts.line_items || []).length > 0 || (d.discounts.total_overall_discount || 0) > 0) && (
          <Section title="Discounts Given Today">
            {(d.discounts.line_items || []).map((dx, i) => (
              <div key={i} className="grid grid-cols-12 gap-1 text-xs py-1 border-b border-slate-50">
                <div className="col-span-6 text-slate-700 truncate">{dx.product || dx.product_name || '\u2014'}</div>
                <div className="col-span-2 text-right text-slate-500">{dx.units || 0}</div>
                <div className="col-span-4 text-right text-amber-700">-{formatPHP(dx.total_discount || dx.amount || 0)}</div>
              </div>
            ))}
            {(d.discounts.total_overall_discount || 0) > 0 && (
              <Row label="Overall (invoice-level) discounts" value={`-${formatPHP(d.discounts.total_overall_discount)}`} negative sub />
            )}
          </Section>
        )}

        {/* 11. Permanent Price Changes */}
        {d.price_changes && Array.isArray(d.price_changes.rows) && d.price_changes.rows.length > 0 && (
          <Section title={`Permanent Price Changes (${d.price_changes.count || d.price_changes.rows.length})`}>
            {d.price_changes.rows.map((pc, i) => (
              <div key={i} className="text-xs py-1.5 border-b border-slate-50">
                <p className="font-semibold text-slate-700 truncate">{pc.product || pc.product_name || '\u2014'}</p>
                <div className="grid grid-cols-3 gap-1 mt-0.5 text-[11px]">
                  <div>
                    <span className="text-slate-400">From: </span>
                    {pc.is_initial_price ? (
                      <span className="text-slate-500 italic">(initial)</span>
                    ) : (
                      <span className="text-slate-700">{formatPHP(pc.from_price || pc.from || 0)}</span>
                    )}
                  </div>
                  <div>
                    <span className="text-slate-400">To: </span>
                    <span className="text-emerald-700 font-semibold">{formatPHP(pc.to_price || pc.to || 0)}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Last Pur: </span>
                    <span className="text-slate-600">{formatPHP(pc.last_purchase || pc.last_pur || 0)}</span>
                  </div>
                </div>
                {(pc.approved_by || pc.ok_by) && (
                  <p className="text-[10px] text-slate-400 mt-0.5">OK by: {pc.approved_by || pc.ok_by}</p>
                )}
              </div>
            ))}
          </Section>
        )}

        {/* Footer Totals */}
        <Section title="Footer Summary">
          {(d.gross_sales || 0) > 0 && <Row label="Gross Sales" value={formatPHP(d.gross_sales)} />}
          {(d.net_sales || 0) > 0 && <Row label="Net Sales" value={formatPHP(d.net_sales)} bold />}
          {(d.cogs || 0) > 0 && <Row label="COGS" value={formatPHP(d.cogs)} sub muted />}
          {(c.safe_balance || 0) > 0 && <Row label="Safe Balance" value={formatPHP(c.safe_balance)} />}
          {c.id && <Row label="Closing ID" value={String(c.id).slice(0, 12) + '\u2026'} sub muted />}
        </Section>

        <p className="text-[10px] text-slate-400 text-center pt-2">
          Closed by {c.closed_by_name || c.finalized_by_name || '\u2014'} \u00B7 {c.closed_at_iso ? new Date(c.closed_at_iso).toLocaleString('en-PH') : ''}
        </p>
        <p className="text-[10px] text-slate-400 text-center">
          Link expires {data.expires_at ? new Date(data.expires_at).toLocaleDateString('en-PH') : 'soon'}.
        </p>
      </div>

      {/* Sticky download bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg p-3 z-50">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={downloadPdf} disabled={downloading}
            className="w-full bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
            data-testid="zr-share-download"
          >
            <Download size={18} />
            {downloading ? 'Preparing PDF\u2026' : 'Download Detailed PDF'}
          </button>
          {downloadError && (
            <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 flex items-start gap-2" data-testid="zr-share-download-error">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold">Download failed</p>
                <p className="leading-snug">{downloadError}</p>
                <p className="text-[10px] text-red-500 mt-0.5">The report above is still valid \u2014 tap the button to retry.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value, accent }) {
  const colors = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    violet: 'bg-violet-50 border-violet-200 text-violet-700',
    red: 'bg-red-50 border-red-200 text-red-700',
  };
  return (
    <div className={`rounded-xl border p-3 ${colors[accent] || colors.emerald}`}>
      <p className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</p>
      <p className="text-base font-extrabold mt-0.5 leading-tight">{value}</p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-lg border-[var(--border-default)] p-3 shadow-sm">
      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{title}</h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value, sub, bold, negative, muted, highlight }) {
  return (
    <div className={`flex justify-between text-sm ${sub ? 'pl-3 text-xs' : ''} ${bold ? 'font-bold' : ''}`}>
      <span className={muted ? 'text-slate-400' : highlight ? 'text-indigo-700' : 'text-slate-600'}>{label}</span>
      <span className={` ${negative ? 'text-red-600' : highlight ? 'text-indigo-700 font-semibold' : muted ? 'text-slate-400' : 'text-slate-800'}`}>{value}</span>
    </div>
  );
}

function DetailLine({ primary, secondary, value, valueClass }) {
  return (
    <div className="flex items-start justify-between gap-2 text-xs py-1.5 border-t border-slate-50">
      <div className="flex-1 min-w-0">
        <p className="text-slate-700 truncate font-medium">{primary}</p>
        {secondary && <p className="text-slate-400 text-[10px] truncate">{secondary}</p>}
      </div>
      <div className={` font-semibold whitespace-nowrap ${valueClass || 'text-slate-700'}`}>{value}</div>
    </div>
  );
}
